import React, { useEffect, useRef } from "react";
import { playTaSound, playTammmSound } from "../lib/sound";

/**
 * CardConfettiCannon — full-viewport <canvas> particle system that fires the
 * "TA - TA - TAMMM" three-beat celebration from the BOTTOM edge of a target
 * card. Used for the Foundation save celebration on the Műhely.
 *
 *   beat 1, t=0ms    — LEFT cannon, 22 particles, "TA"   (short pop)
 *   beat 2, t=180ms  — RIGHT cannon, 22 particles, "TA"   (short pop)
 *   beat 3, t=420ms  — ALL cannons, 60+60+80 particles, "TAMMM" (BUMMM)
 *
 * The 180ms / 240ms gaps create the rhythm and must NOT be randomized.
 *
 * Implementation notes:
 *   • Single canvas, single requestAnimationFrame loop.
 *   • Particles are tiny rotating rectangles (paper-confetti look) with
 *     air resistance + gravity + low-frequency flutter.
 *   • Loop stops once the array empties; canvas is unmounted in cleanup.
 *   • prefers-reduced-motion → skipped entirely; onDone fires immediately.
 *   • Mobile (< 700px viewport): particle counts × 0.6 to keep frame-rate.
 *   • devicePixelRatio canvas scaling for crisp retina rendering.
 *
 * Stability:
 *   • targetRef is a NEW object identity every Dashboard re-render — keeping
 *     it in the firing useEffect's dep array used to cause 4-5 rapid remounts
 *     during the celebration window, each one re-firing TA #1 before being
 *     torn down. We mirror it into a stable in-component ref and run the
 *     firing effect on `active` only.
 *
 * Props:
 *   active     boolean  gates the firing sequence
 *   targetRef  ref      points at the card DOM node (bottom = cannon line)
 *   onDone     fn       called once all particles have left the screen
 */

const PALETTE = [
  "#5e50f7", "#8a7cff", "#a89bff",   // brand purples
  "#ffd700", "#ffed4e",              // golds
  "#4cb585", "#7dd3a0",              // DONE greens
  "#ff6b9d", "#ff9ec7",              // pinks
  "#4ec9f7", "#ffffff",              // accent + white
];

const WAVES = [
  { t: 0,   counts: { left: 22, right: 0,  middle: 0  }, power: 10, sound: playTaSound },
  { t: 180, counts: { left: 0,  right: 22, middle: 0  }, power: 10, sound: playTaSound },
  { t: 420, counts: { left: 60, right: 60, middle: 80 }, power: 15, sound: playTammmSound },
];

const CANNON_ANGLES = {
  left:   -Math.PI / 3,        // 60° up-and-right
  right:  -2 * Math.PI / 3,    // 60° up-and-left
  middle: -Math.PI / 2,        // straight up
};

function prefersReducedMotion() {
  try {
    return window.matchMedia &&
      window.matchMedia("(prefers-reduced-motion: reduce)").matches;
  } catch {
    return false;
  }
}

function makeParticle(originX, originY, baseAngle, power) {
  const angle = baseAngle + (Math.random() - 0.5) * 0.35; // ±0.175 rad
  const speed = power * (0.7 + Math.random() * 0.3);
  return {
    x: originX,
    y: originY,
    vx: Math.cos(angle) * speed,
    vy: Math.sin(angle) * speed,
    width: 4 + Math.random() * 4,    // 4-8px
    height: 7 + Math.random() * 4,   // 7-11px
    color: PALETTE[(Math.random() * PALETTE.length) | 0],
    rotation: Math.random() * Math.PI * 2,
    rotationSpeed: (Math.random() - 0.5) * 0.3,    // ±0.15
    flutter: Math.random() * 0.05,                 // 0-0.05
    flutterPhase: Math.random() * Math.PI * 2,
    life: 1.0,
  };
}

export default function CardConfettiCannon({ active, targetRef, onDone, zIndex = 50, containerRef = null }) {
  const canvasRef = useRef(null);
  const particlesRef = useRef([]);
  const rafRef = useRef(0);
  const tickRef = useRef(0);

  // Mirror props into stable refs so the firing useEffect can read the
  // latest values without depending on changing object identities.
  const onDoneRef = useRef(onDone);
  onDoneRef.current = onDone;
  const targetRefStable = useRef(targetRef);
  useEffect(() => {
    targetRefStable.current = targetRef;
  }, [targetRef]);

  // Firing effect — depends ONLY on `active`. Without this, every Dashboard
  // re-render (which produces a new {current: ...} targetRef object) would
  // unmount the canvas mid-sequence and re-fire TA #1.
  useEffect(() => {
    if (!active) return undefined;
    if (prefersReducedMotion()) {
      onDoneRef.current?.();
      return undefined;
    }
    const target = targetRefStable.current?.current;
    if (!target) {
      onDoneRef.current?.();
      return undefined;
    }

    // Mobile scaling — fewer particles below 700px.
    const isSmall = window.innerWidth < 700;
    const countScale = isSmall ? 0.6 : 1;

    // Set up the canvas overlay (fixed, full viewport, retina-aware).
    // Append to a CUSTOM container ha kapunk ilyet — pl. modal overlay
    // belsejébe, így a `backdrop-filter: blur` által okozott
    // stacking-context csapdát megkerüljük (a canvas az overlay belső
    // children listájában, FELÜL renderelődik a card mellett, NEM a body
    // szintjén ahonnan a backdrop-blur eltüntetné).
    const canvas = document.createElement("canvas");
    canvas.setAttribute("data-testid", "card-confetti-cannon");
    canvas.style.position = "fixed";
    canvas.style.left = "0";
    canvas.style.top = "0";
    canvas.style.width = "100vw";
    canvas.style.height = "100vh";
    canvas.style.pointerEvents = "none";
    canvas.style.zIndex = String(zIndex);
    const dpr = Math.min(window.devicePixelRatio || 1, 2);
    canvas.width = window.innerWidth * dpr;
    canvas.height = window.innerHeight * dpr;
    const parent = containerRef?.current || document.body;
    parent.appendChild(canvas);
    canvasRef.current = canvas;

    const ctx = canvas.getContext("2d");
    if (ctx) ctx.scale(dpr, dpr);

    // Cannon positions are computed PER WAVE so a mid-flight scroll/resize
    // does not throw the new beat to a stale anchor point.
    const computeCannons = () => {
      const r = target.getBoundingClientRect();
      const yLine = r.bottom - 10;
      return {
        left:   { x: r.left + 30,           y: yLine },
        right:  { x: r.right - 30,          y: yLine },
        middle: { x: r.left + r.width / 2,  y: yLine },
      };
    };

    const fireWave = (wave) => {
      const cannons = computeCannons();
      const list = particlesRef.current;
      const fire = (key) => {
        const n = Math.round((wave.counts[key] || 0) * countScale);
        if (!n) return;
        const origin = cannons[key];
        const baseAngle = CANNON_ANGLES[key];
        for (let i = 0; i < n; i++) {
          list.push(makeParticle(origin.x, origin.y, baseAngle, wave.power));
        }
      };
      fire("left");
      fire("right");
      fire("middle");
      try { wave.sound?.(); } catch {}
    };

    const timeouts = WAVES.map((w) => setTimeout(() => fireWave(w), w.t));

    const loop = () => {
      const c = canvasRef.current;
      if (!c) return;
      const cctx = c.getContext("2d");
      if (!cctx) return;
      tickRef.current += 1;
      const tick = tickRef.current;
      cctx.clearRect(0, 0, c.width, c.height);

      const list = particlesRef.current;
      const viewportH = window.innerHeight;
      let alive = 0;

      for (let i = 0; i < list.length; i++) {
        const p = list[i];
        if (p.life <= 0) continue;

        // Physics
        p.vx *= 0.99;
        p.vy *= 0.99;
        p.vy += 0.13;
        p.vx += Math.sin(tick * 0.05 + p.flutterPhase) * p.flutter;
        p.x += p.vx;
        p.y += p.vy;
        p.rotation += p.rotationSpeed;

        if (p.y > viewportH + 30) p.life -= 0.015;
        if (p.life <= 0) continue;

        // 3D-tumbling effect: width modulated by cosine to scale 30%-100%.
        const tumble = Math.cos(tick * 0.1 + p.flutterPhase) * 0.5 + 0.5;
        const w = Math.max(1, p.width * (0.3 + tumble * 0.7));

        cctx.save();
        cctx.translate(p.x, p.y);
        cctx.rotate(p.rotation);
        cctx.globalAlpha = Math.max(0, Math.min(1, p.life));
        cctx.fillStyle = p.color;
        cctx.fillRect(-w / 2, -p.height / 2, w, p.height);
        cctx.restore();
        alive += 1;
      }

      if (tick % 30 === 0 && list.length > alive * 2 + 20) {
        particlesRef.current = list.filter((p) => p.life > 0);
      }

      // Stop when all waves have fired (>~0.6s) AND no particles remain.
      const allWavesFired = tick > 36;
      if (alive === 0 && allWavesFired) {
        rafRef.current = 0;
        try { c.parentNode?.removeChild(c); } catch { /* swallow */ }
        canvasRef.current = null;
        particlesRef.current = [];
        onDoneRef.current?.();
        return;
      }
      rafRef.current = requestAnimationFrame(loop);
    };

    rafRef.current = requestAnimationFrame(loop);

    return () => {
      timeouts.forEach(clearTimeout);
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
      rafRef.current = 0;
      const c = canvasRef.current;
      if (c) { try { c.parentNode?.removeChild(c); } catch { /* swallow */ } }
      canvasRef.current = null;
      particlesRef.current = [];
      tickRef.current = 0;
    };
  }, [active]); // eslint-disable-line react-hooks/exhaustive-deps

  return null;
}
